import { ErrorMessage } from '@hookform/error-message';
import { SendFreightDocumentsForReturnShipmentRequest } from '~api/som';

import { SubmitHandler, useForm } from 'react-hook-form';

import { Button, FormControl, FormItem, ListLayout, LoadingButton, Modal, TextInput } from '@ingrid/luterkort-react';

import { MutationBanner } from '~src/components/MutationNotification/MutationNotification';
import { useTranslation } from '~src/i18n/use-translation';
import { useToast } from '~src/main/hooks/useToast';

import { useSendFreightDocumentsMutation } from '../mutations';

type FormValues = Pick<SendFreightDocumentsForReturnShipmentRequest, 'email'>;

interface Props {
  shipmentId: string | undefined;
  onSuccess?: () => void;
}

const Validate = () => {
  const { t } = useTranslation();

  return {
    email: {
      required: t('common.fieldRequired', {
        field: t('common.email'),
      }),
      pattern: {
        message: t('addressBook.details.validateEmailFormat'),
        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      },
    },
  };
};

export function FreightDocumentDialog({ shipmentId, onSuccess }: Props) {
  const freightDocumentsMutation = useSendFreightDocumentsMutation();
  const { toast } = useToast();

  const { t } = useTranslation();

  const { handleSubmit, register, formState, reset } = useForm<FormValues>({
    defaultValues: {
      email: '',
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async values => {
    await freightDocumentsMutation.mutateAsync({ email: values.email, return_shipment_id: shipmentId });

    onSuccess?.();
    toast({ variant: 'success', title: t('freightDocuments.modal.formSuccess') });
    reset();
  };

  return (
    <Modal.Content>
      <FormControl.Root>
        <FormControl.Form onSubmit={handleSubmit(onSubmit)} style={{ display: 'none' }} />

        <Modal.Header title={t('freightDocuments.modal.title')} />

        <Modal.Body css={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <MutationBanner mutation={freightDocumentsMutation} />

          <FormItem.Root>
            <FormItem.Label>{t('common.customerEmailAddress')}</FormItem.Label>
            <FormItem.Control invalid={!!formState.errors?.email?.message}>
              <TextInput aria-required="true" {...register('email', Validate().email)} />
            </FormItem.Control>
            <ErrorMessage as={FormItem.ErrorText} errors={formState.errors} name="email" />
          </FormItem.Root>

          <ListLayout.Column as="dl">
            <ListLayout.Stack as="div">
              <ListLayout.SecondaryText as="dd">{t('common.attachedDocuments')}</ListLayout.SecondaryText>
              <ListLayout.PrimaryText as="dt">return-label.pdf</ListLayout.PrimaryText>
            </ListLayout.Stack>
          </ListLayout.Column>
        </Modal.Body>

        <Modal.Footer>
          <Modal.CloseButton>
            <Button variant="secondary">{t('common.cancel')}</Button>
          </Modal.CloseButton>

          <FormControl.SubmitButton asChild>
            <LoadingButton loading={freightDocumentsMutation.isPending}>{t('common.sendDocuments')}</LoadingButton>
          </FormControl.SubmitButton>
        </Modal.Footer>
      </FormControl.Root>
    </Modal.Content>
  );
}
